// Hooks con React Query: el panel conoce el backend por tipos.
// Usar estos hooks en páginas/componentes → caché, reintentos y tipos gratis.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  API_ROUTES,
  whatsappSessionRoutes,
  type CreateWhatsappSessionInput,
  type Docs,
  type RenameWhatsappSessionInput,
  type SetSessionWebhookInput,
  type WhatsappLogs,
  type WhatsappSession,
  type WhatsappSessions,
  type WhatsappStatus,
} from "@shared/api";
import { apiDelete, apiGet, apiPatch, apiPost } from "@client/api/client";

export const queryKeys = {
  sessions: ["whatsapp", "sessions"],
  session: (id: string) => ["whatsapp", "session", id],
  logs: (id: string) => ["whatsapp", "logs", id],
  docs: ["docs"],
} as const;

// Documentación: solo se pide cuando el usuario abre la vista (autenticado).
export function useDocs(enabled: boolean) {
  return useQuery({
    queryKey: queryKeys.docs,
    queryFn: () => apiGet<Docs>(API_ROUTES.docs),
    enabled,
    staleTime: Number.POSITIVE_INFINITY,
  });
}

// `live` = el WebSocket está abierto: no hace falta polling (el backend ya
// empuja los cambios). Si cae el socket, se reactiva el refresco periódico.
export function useWhatsappSessions(live = false) {
  return useQuery({
    queryKey: queryKeys.sessions,
    queryFn: () => apiGet<WhatsappSessions>(API_ROUTES.whatsappSessions),
    refetchInterval: live ? false : 20_000,
  });
}

export function useWhatsappSessionLogs(id: string | null, live = false) {
  return useQuery({
    queryKey: queryKeys.logs(id ?? "none"),
    queryFn: () => apiGet<WhatsappLogs>(whatsappSessionRoutes(id ?? "").logs),
    enabled: id !== null,
    refetchInterval: live ? false : 10_000,
  });
}

export function useCreateWhatsappSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateWhatsappSessionInput) =>
      apiPost<WhatsappSession, CreateWhatsappSessionInput>(API_ROUTES.whatsappSessions, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.sessions });
    },
  });
}

export function useRenameWhatsappSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: RenameWhatsappSessionInput }) =>
      apiPatch<WhatsappSession, RenameWhatsappSessionInput>(
        whatsappSessionRoutes(id).status,
        input,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.sessions });
    },
  });
}

export function useDeleteWhatsappSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiDelete<{ id: string }>(whatsappSessionRoutes(id).status),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.sessions });
    },
  });
}

export function useRotateWhatsappSessionToken() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiPost<WhatsappSession, Record<string, never>>(whatsappSessionRoutes(id).token, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.sessions });
    },
  });
}

export function useSetWhatsappSessionWebhook() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: SetSessionWebhookInput }) =>
      apiPatch<WhatsappSession, SetSessionWebhookInput>(whatsappSessionRoutes(id).webhook, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.sessions });
    },
  });
}

export function useWhatsappSessionConnect() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiPost<WhatsappStatus, Record<string, never>>(whatsappSessionRoutes(id).connect, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.sessions });
    },
  });
}

export function useWhatsappSessionLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiPost<WhatsappStatus, Record<string, never>>(whatsappSessionRoutes(id).logout, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.sessions });
    },
  });
}
